/**
 * PROJECT-REVIEW-TOAST.WIDGET
 * Small card of the owl after a step is confirmed during the review of the project : what is left, and the next step to look at
 */
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Link from "@/components/link"
import { faBellSlash, faListCheck, IconDefinition } from "@fortawesome/free-solid-svg-icons"

interface Props {
  title: string //"Step confirmed"
  subtitle: string //What is left (already translated : the toast is displayed out of the translation context)
  progress?: { ready: number; total: number } //Steps ready on the total : a thin bar under the subtitle
  actionLabel?: string //Next step to look at, or create the survey
  actionIcon?: IconDefinition //The icon of that step, in a round at the left of the label (no arrow)
  onAction?: Function
  reviewLabel?: string
  onReview?: Function
  stopLabel: string
  onStop: Function
}

const GREEN: string = "var(--idt-darkgreen)"

export default function ProjectReviewToast(props: Props) {
  return (
    <div style={{ padding: "2px 2px 0" }}>
      <div className="flex" style={{ gap: 14, alignItems: "flex-start" }}>
        <motion.img
          src={require("@/assets/owl.png")}
          alt=""
          initial={{ rotate: -12, scale: 0.8 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 14 }}
          style={{ width: 48, height: "auto", flexShrink: 0, marginTop: 2 }}
        />

        <div className="flex1 dark-t" style={{ minWidth: 0 }}>
          <b>{props.title}</b>
          <div className="grey-t text-sm" style={{ marginTop: 2 }}>
            {props.subtitle}
          </div>
          {props.progress && props.progress.total > 0 && (
            <div
              style={{
                height: 4,
                borderRadius: 999,
                marginTop: 17,
                background: "color-mix(in srgb, var(--idt-darkgreen) 14%, white)",
                overflow: "hidden"
              }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: Math.round((props.progress.ready / props.progress.total) * 100) + "%" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ height: "100%", borderRadius: 999, background: GREEN }}
              />
            </div>
          )}
        </div>
      </div>
      {/* The next step and the links go under the owl, on the whole width of the card : no blank under it */}
      {props.actionLabel && (
        <div
          className="flex cursor-pointer text-sm"
          style={{
            marginTop: 5,
            padding: "8px 12px 8px 8px",
            borderRadius: 12,
            alignItems: "center",
            gap: 10,
            fontWeight: 700,
            color: GREEN,
            background: "color-mix(in srgb, var(--idt-darkgreen) 10%, white)"
          }}
          onClick={() => props.onAction?.()}>
          {props.actionIcon && (
            <span
              className="flex"
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "white",
                background: GREEN
              }}>
              <FontAwesomeIcon icon={props.actionIcon} />
            </span>
          )}
          <span>{props.actionLabel}</span>
        </div>
      )}

      <div
        className="flex grey-t text-sm"
        style={{
          marginTop: 14,
          padding: "8px 2px 0",
          borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          justifyContent: "space-between",
          whiteSpace: "nowrap",
          gap: 16
        }}>
        {props.reviewLabel ? (
          <Link isWithoutMargin icon={faListCheck} onClick={() => props.onReview?.()}>
            {props.reviewLabel}
          </Link>
        ) : (
          <span />
        )}
        <Link isWithoutMargin icon={faBellSlash} onClick={() => props.onStop()}>
          {props.stopLabel}
        </Link>
      </div>
    </div>
  )
}
